/**
 * Pure layout helpers for the Employer digital footprint map — heat radii and
 * geographic span used to keep purchase blobs visible when fitting both pins.
 */

export type LatLng = { lat: number; lng: number };

/** When purchase cluster and employer are farther apart, open on purchases first. */
export const DISTANT_EMPLOYER_SPAN_KM = 250;

function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function purchaseCentroid(points: LatLng[]): LatLng | null {
  if (points.length === 0) return null;
  return {
    lat: points.reduce((s, p) => s + p.lat, 0) / points.length,
    lng: points.reduce((s, p) => s + p.lng, 0) / points.length,
  };
}

/**
 * Distant HQ: zoom to purchase heat first so blobs stay visible; nearby: fit both.
 */
export function pickInitialFootprintFocus(
  purchasePoints: LatLng[],
  employerPoint: LatLng | null,
): 'all' | 'purchases' {
  if (purchasePoints.length === 0 || !employerPoint) return 'all';
  const centroid = purchaseCentroid(purchasePoints);
  if (!centroid) return 'all';
  return haversineKm(centroid, employerPoint) > DISTANT_EMPLOYER_SPAN_KM
    ? 'purchases'
    : 'all';
}

/** Approximate diagonal span (km) across a set of map points. */
export function computeBoundsSpanKm(points: LatLng[]): number {
  if (points.length < 2) return 0;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  const centerLat = (minLat + maxLat) / 2;
  const latKm = (maxLat - minLat) * 111;
  const lngKm = (maxLng - minLng) * 111 * Math.cos((centerLat * Math.PI) / 180);
  return Math.sqrt(latKm * latKm + lngKm * lngKm);
}

/** Circle radii (meters) and fill opacity scaled to the visible pin span. */
export function computeFootprintHeatRadii(spanKm: number): {
  outerR: number;
  midR: number;
  coreR: number;
  fillOpacity: number;
} {
  const outerR =
    spanKm > 400
      ? Math.min(120_000, Math.max(18_000, spanKm * 60))
      : spanKm > 200
        ? Math.min(95_000, Math.max(12_000, spanKm * 55))
        : spanKm > 40
          ? Math.min(12_000, Math.max(2_500, spanKm * 80))
          : 1400;
  const midR = Math.max(180, outerR * 0.32);
  const coreR = Math.max(80, outerR * 0.09);
  const fillOpacity = spanKm > 400 ? 0.35 : spanKm > 200 ? 0.28 : 0.18;
  return { outerR, midR, coreR, fillOpacity };
}
